import {
  ConversationsData,
  Id,
  Message,
  MessagesData,
  ReadByCountsData,
  MessagesResponse as Response,
  User,
  convertAddOrUpdateOrPinnedEvent,
  convertMember,
  convertMentions,
  convertReactions,
  removeOldMessages,
} from '@nextforce/api'
import { QueryClient } from '@tanstack/react-query'
import { ChatConfig } from 'chat/config/types'
import keys from 'chat/shared/queries/keys'
import { UpdateReadByCountWebsocketEvent } from 'chat/websocket/types'
import { produce } from 'immer'
import { differenceWith, isEqual } from 'lodash'
import normalizeUrl from 'shared/normalize_url_util'

type MessageMap = {
  [conversationId: string]: Message
}

export function getRecentMessagesFromCache(client: QueryClient): MessageMap {
  const data = client.getQueryData<MessagesData>(keys.messages())
  if (!data) {
    return {}
  }
  const messages = data.messages.filter(message => !message.isDeleted)

  return messages.reduce<MessageMap>((map, message) => {
    return { ...map, [message.conversationId]: message }
  }, {})
}

export async function addMessagesDataToCache(
  client: QueryClient,
  events: Response.AddMessageEvent[],
  chatConfig: ChatConfig | undefined
) {
  const key = keys.messages()
  const data = client.getQueryData<MessagesData>(key)
  if (!data) {
    return
  }

  await client.cancelQueries({ queryKey: key })
  client.setQueryData<MessagesData>(
    key,
    produce(data, updatedData => {
      events.forEach(event => {
        const index = updatedData.messages.findIndex(m => m.id === event.id)
        if (index >= 0) {
          updatedData.messages[index] = convertAddOrUpdateOrPinnedEvent(event)
        } else {
          updatedData.messages.push(convertAddOrUpdateOrPinnedEvent(event))
        }
      })
      if (chatConfig?.maxMessageAge) {
        removeOldMessages(updatedData.messages, chatConfig.maxMessageAge)
      }
    })
  )
  await updateConversationRecentMessages(client)
}

export async function updateConversationRecentMessages(client: QueryClient) {
  const key = keys.conversations()
  const data = client.getQueryData<ConversationsData>(key)

  if (!data) {
    return
  }

  const recentMessageMap = getRecentMessagesFromCache(client)

  client.setQueryData<ConversationsData>(
    key,
    produce(data, updatedData => {
      updatedData.forEach(conversation => {
        conversation.status = {
          ...conversation.status,
          recentMessage: recentMessageMap[conversation.id],
        }
      })
    })
  )
}

export async function removeMessageDataFromCache(
  client: QueryClient,
  event: Response.RemoveMessageEvent
) {
  const key = keys.messages()
  const data = client.getQueryData<MessagesData>(key)

  if (!data) {
    return
  }

  const index = data.messages.findIndex(m => m.id === event.message_id)

  if (index < 0) {
    return
  }

  // capture conversationId from cache for downstream invalidations
  const conversationId = data.messages[index].conversationId

  await client.cancelQueries({ queryKey: key })
  client.setQueryData<MessagesData>(
    key,
    produce(data, updatedData => {
      updatedData.messages[index] = {
        ...updatedData.messages[index],
        isDeleted: true,
        deletedBy: convertMember(event.user),
      }
    })
  )
  await updateConversationRecentMessages(client)
  // Ensure pinned messages list reflects removal immediately on next fetch
  if (conversationId) {
    await client.invalidateQueries({
      queryKey: keys.pinnedMessages(conversationId),
    })
  }
}

export async function updateReactionData(
  client: QueryClient,
  event: Response.AddReactionEvent,
  user?: User
) {
  const key = keys.messages()
  const data = client.getQueryData<MessagesData>(key)

  if (!data || !data.messages) {
    return
  }
  const index = data.messages.findIndex(m => m.id === event.message_id)

  if (index === -1) {
    return
  }

  const reactionsInQuery = data.messages[index].reactions.filter(r => {
    return r.userId === user?.id
  })

  const reactionsInEvent = convertReactions(
    event.reactions.filter(r => {
      return r.user_id === user?.id
    })
  )

  const reactionsToRemove = differenceWith(
    reactionsInQuery,
    reactionsInEvent,
    isEqual
  )

  // capture conversationId from cache for downstream invalidations
  const conversationId = data.messages[index].conversationId

  await client.cancelQueries({ queryKey: key })
  client.setQueryData<MessagesData>(
    key,
    produce(data, updatedData => {
      if (reactionsToRemove.length > 0) {
        const indexToRemove = updatedData.messages[index].reactions.findIndex(
          r =>
            r.userId === reactionsToRemove[0].userId &&
            r.emoji === reactionsToRemove[0].emoji
        )
        updatedData.messages[index].reactions.splice(indexToRemove, 1)
      } else {
        updatedData.messages[index].reactions = convertReactions(
          event.reactions
        )
      }
    })
  )
  // Ensure pinned messages list reflects reaction updates on next fetch
  if (conversationId) {
    await client.invalidateQueries({
      queryKey: keys.pinnedMessages(conversationId),
    })
  }
}

export async function addMentionDataToCache(
  client: QueryClient,
  event: Response.CreateMentionsEvent
) {
  const key = keys.messages()
  const data = client.getQueryData<MessagesData>(key)

  if (!data) {
    return
  }
  const index = data.messages.findIndex(m => m.id === event.message_id)

  await client.cancelQueries({ queryKey: key })
  client.setQueryData<MessagesData>(
    key,
    produce(data, updatedData => {
      updatedData.messages[index].mentions = convertMentions(event.mentions)
    })
  )
}

export async function updateReadByCount(
  client: QueryClient,
  event: UpdateReadByCountWebsocketEvent
) {
  const key = keys.readByCounts(event.conversationId)
  let data: ReadByCountsData | undefined =
    client.getQueryData<ReadByCountsData>(key)
  if (!data) {
    data = {}
  }
  client.setQueryData<ReadByCountsData>(
    key,
    produce(data, updatedData => {
      event.messageIds.forEach(messageId => {
        if (updatedData[messageId] !== undefined) {
          updatedData[messageId]++
        } else {
          updatedData[messageId] = 1
        }
      })
    })
  )
}

export async function setReadByCount(
  client: QueryClient,
  conversationId: Id,
  messageId: Id,
  count: number
) {
  const key = keys.readByCounts(conversationId)
  let data: ReadByCountsData | undefined =
    client.getQueryData<ReadByCountsData>(key)
  if (!data) {
    data = {}
  }
  client.setQueryData<ReadByCountsData>(
    key,
    produce(data, updatedData => {
      updatedData[messageId] = count
    })
  )
}

export const cleanUrl = (url?: string) => {
  if (!url) {
    return ''
  }
  let normalizedUrl = normalizeUrl(url, {
    stripWWW: false,
    stripTextFragment: true,
    removeQueryParameters: ['ref', 'utm_*', 'session', 'fbclid'],
    removeTrailingSlash: true,
  })
  if (!url.match(/^[a-zA-Z][a-zA-Z\d+\-.]*:/)) {
    if (url.toLowerCase().startsWith('www.')) {
      normalizedUrl = `http://${url}`
    } else {
      normalizedUrl = `http://www.${url}`
    }
  }

  return normalizedUrl
}

export const extractUrl = (text: string) => {
  const urlPattern = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi
  const match = text.match(urlPattern)
  return match ? match[0] : ''
}
